import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardHeader from '../../../../components/DashboardHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import UnderConstruction from '../../../../components/UnderConstruction';
import Agenda from './myspace/agenda';
import { FaRegCalendarAlt } from 'react-icons/fa';
import './my_space.css';
import { auth, db } from '../../../../auth/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import cacheManager from '../../../../auth/cache_manager';
import { useAuthStore } from '../../../../auth/state';

const MySpace = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { userRole } = useAuthStore();
  
  // # Parse tab from URL query parameter
  const getTabFromQuery = () => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    return tabs.some(tab => tab.id === tabParam) ? tabParam : 'notebook';
  };
  
  // # Track active tab
  const [activeTab, setActiveTab] = useState('notebook');

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Define tabs
  const tabs = [
    { id: 'notebook', label: 'My Notebook' },
    { id: 'checklist', label: 'Checklist' },
    { id: 'agenda', label: 'Checkup Agenda' },
    { id: 'mentee_progress', label: 'Mentee Progress' }
  ];
  
  // # Update active tab when URL query changes
  useEffect(() => {
    const tabFromQuery = getTabFromQuery();
    setActiveTab(tabFromQuery);
  }, [location.search]);

  // # Handle calendar navigation based on role
  const handleCalendarClick = () => {
    const basePath = userRole === 'tutor' ? '/tutor' : '/admin';
    navigate(`${basePath}/mentorship/calendar`, { replace: true });
  };

  // # Handle tab change with role-aware navigation
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    // # Update URL without refreshing page
    const basePath = userRole === 'tutor' ? '/tutor' : '/admin';
    navigate(`${basePath}/mentorship/study?tab=${tabId}`, { replace: true });
  };

  // # Render content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'agenda':
        return <Agenda isDarkMode={isDarkMode} />;
      case 'notebook':
      case 'checklist':
      case 'mentee_progress':
      default:
        return <UnderConstruction pageName={tabs.find(t => t.id === activeTab)?.label || ''} />;
    }
  };

  return (
    <div className={`mntr_spc_container ${isDarkMode ? 'mntr_spc_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="mntr_spc_main-content">
        {/* # Title Section */}
        <div className="mntr_spc_title-section">
          <h2>My Space</h2>
        </div>

        {/* # Tabs Section */}
        <div className="mntr_spc_tabs-container">
          <div className="mntr_spc_tabs-wrapper">
            <div className="mntr_spc_tabs">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  className={`mntr_spc_tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <button 
              className="mntr_spc_calendar-btn"
              onClick={handleCalendarClick}
              title="View Calendar"
            >
              <FaRegCalendarAlt className="mntr_spc_calendar-icon" />
            </button>
          </div>

          {/* # Content Section */}
          <div className="mntr_spc_content">
            {renderTabContent()}
          </div>
        </div>
      </div>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
};

export default MySpace;